import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, Button, Space, Tag, Tabs, message, Select, Tooltip, Modal, Descriptions, Empty, Drawer, Timeline } from 'antd';
import { ReloadOutlined, RetweetOutlined, MailOutlined, SendOutlined, InboxOutlined, ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined, ClusterOutlined, CloudServerOutlined, NodeIndexOutlined } from '@ant-design/icons';
import { adminApi } from '../../services/api';
import { formatBytes } from '../../../shared/utils/format';

const Stats = () => {
    const [clusters, setClusters] = useState([]);
    const [selectedCluster, setSelectedCluster] = useState(null);
    const [stats, setStats] = useState(null);
    const [queue, setQueue] = useState([]);
    const [queueStats, setQueueStats] = useState(null);
    const [deliveryHistory, setDeliveryHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [queueLoading, setQueueLoading] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('stats');
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const [historyPagination, setHistoryPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);

    // Relay hosts / cluster stats
    const [relayHosts, setRelayHosts] = useState([]);
    const [relayLoading, setRelayLoading] = useState(false);
    const [clusterStats, setClusterStats] = useState(null);
    const [clusterStatsLoading, setClusterStatsLoading] = useState(false);

    // Message delivery trace
    const [traceDrawerVisible, setTraceDrawerVisible] = useState(false);
    const [traceLoading, setTraceLoading] = useState(false);
    const [traceRecords, setTraceRecords] = useState([]);
    const [traceMessageId, setTraceMessageId] = useState(null);

    const fetchClusters = async () => {
        try {
            const res = await adminApi.get('/webmail/clusters');
            if (res.data.code === 0) {
                const list = res.data.data.list || [];
                setClusters(list);
                if (!selectedCluster) {
                    const defaultCluster = list.find(c => c.is_default);
                    if (defaultCluster) {
                        setSelectedCluster(defaultCluster.id);
                    } else if (list.length > 0) {
                        setSelectedCluster(list[0].id);
                    }
                }
            }
        } catch (error) {
            message.error('获取集群列表失败');
        }
    };

    const fetchStats = async () => {
        if (!selectedCluster) return;
        setLoading(true);
        try {
            const res = await adminApi.get('/webmail/stats', {
                params: { cluster_id: selectedCluster }
            });
            if (res.data.code === 0) {
                setStats(res.data.data);
            }
        } catch (error) {
            message.error('获取统计数据失败');
        }
        setLoading(false);
    };

    const fetchQueue = async (page = 1, pageSize = 20) => {
        if (!selectedCluster) return;
        setQueueLoading(true);
        try {
            const [queueRes, statsRes] = await Promise.all([
                adminApi.get('/webmail/queue', {
                    params: { cluster_id: selectedCluster, page, per_page: pageSize }
                }),
                adminApi.get('/webmail/queue-stats', {
                    params: { cluster_id: selectedCluster }
                })
            ]);
            if (queueRes.data.code === 0) {
                const data = queueRes.data.data;
                setQueue(data.items || data.list || []);
                setPagination({
                    current: data.page || page,
                    pageSize: data.per_page || pageSize,
                    total: data.total || 0,
                });
            }
            if (statsRes.data.code === 0) {
                setQueueStats(statsRes.data.data);
            }
        } catch (error) {
            message.error('获取队列数据失败');
        }
        setQueueLoading(false);
    };

    const fetchDeliveryHistory = async (page = 1, pageSize = 20) => {
        if (!selectedCluster) return;
        setHistoryLoading(true);
        try {
            const res = await adminApi.get('/webmail/delivery-history', {
                params: { cluster_id: selectedCluster, page, per_page: pageSize }
            });
            if (res.data.code === 0) {
                const data = res.data.data;
                setDeliveryHistory(data.items || data.list || []);
                setHistoryPagination({
                    current: data.page || page,
                    pageSize: data.per_page || pageSize,
                    total: data.total || 0,
                });
            }
        } catch (error) {
            message.error('获取投递历史失败');
        }
        setHistoryLoading(false);
    };

    const fetchRelayHosts = async () => {
        if (!selectedCluster) return;
        setRelayLoading(true);
        try {
            const res = await adminApi.get('/webmail/relay-hosts', {
                params: { cluster_id: selectedCluster }
            });
            if (res.data.code === 0) {
                const data = res.data.data;
                setRelayHosts(data.items || data.list || (Array.isArray(data) ? data : []));
            }
        } catch (error) {
            message.error('获取中继主机失败');
        }
        setRelayLoading(false);
    };

    const fetchClusterStats = async () => {
        if (!selectedCluster) return;
        setClusterStatsLoading(true);
        try {
            const res = await adminApi.get('/webmail/cluster/stats', {
                params: { cluster_id: selectedCluster }
            });
            if (res.data.code === 0) {
                setClusterStats(res.data.data);
            }
        } catch (error) {
            message.error('获取集群统计失败');
        }
        setClusterStatsLoading(false);
    };

    const fetchTabData = (tab) => {
        if (tab === 'queue') {
            fetchQueue();
        } else if (tab === 'history') {
            fetchDeliveryHistory();
        } else if (tab === 'relay') {
            fetchRelayHosts();
        } else if (tab === 'cluster') {
            fetchClusterStats();
        }
    };

    // View delivery trace for a single message
    const handleViewTrace = async (messageId) => {
        if (!messageId) return;
        setTraceMessageId(messageId);
        setTraceRecords([]);
        setTraceDrawerVisible(true);
        setTraceLoading(true);
        try {
            const res = await adminApi.get(`/webmail/messages/${messageId}/delivery-history`, {
                params: { cluster_id: selectedCluster }
            });
            if (res.data.code === 0) {
                const data = res.data.data;
                setTraceRecords(data.items || data.list || (Array.isArray(data) ? data : []));
            } else {
                message.error(res.data.message || '获取投递轨迹失败');
            }
        } catch (error) {
            message.error('获取投递轨迹失败');
        }
        setTraceLoading(false);
    };

    useEffect(() => {
        fetchClusters();
    }, []);

    useEffect(() => {
        if (selectedCluster) {
            fetchStats();
            fetchTabData(activeTab);
        }
    }, [selectedCluster]);

    useEffect(() => {
        if (selectedCluster) {
            fetchTabData(activeTab);
        }
    }, [activeTab]);

    const handleRetry = async (id) => {
        try {
            const res = await adminApi.post(`/webmail/queue/${id}/retry`, {
                cluster_id: selectedCluster
            });
            if (res.data.code === 0) {
                message.success('重试已触发');
                fetchQueue(pagination.current, pagination.pageSize);
            } else {
                message.error(res.data.message || '操作失败');
            }
        } catch (error) {
            message.error('操作失败');
        }
    };

    const showDetail = (record) => {
        setSelectedItem(record);
        setDetailModalVisible(true);
    };

    const getStatusTag = (status) => {
        const statusMap = {
            pending: { color: 'blue', icon: <ClockCircleOutlined />, text: '等待中' },
            processing: { color: 'orange', icon: <ReloadOutlined spin />, text: '处理中' },
            sent: { color: 'green', icon: <CheckCircleOutlined />, text: '已发送' },
            delivered: { color: 'green', icon: <CheckCircleOutlined />, text: '已投递' },
            failed: { color: 'red', icon: <CloseCircleOutlined />, text: '失败' },
            deferred: { color: 'gold', icon: <ExclamationCircleOutlined />, text: '延迟' },
            bounced: { color: 'red', icon: <CloseCircleOutlined />, text: '退信' },
        };
        const s = statusMap[status] || { color: 'default', text: status };
        return <Tag color={s.color} icon={s.icon}>{s.text}</Tag>;
    };

    const queueColumns = [
        {
            title: 'ID',
            dataIndex: 'id',
            width: 60,
        },
        {
            title: '发件人',
            dataIndex: 'from',
            ellipsis: true,
        },
        {
            title: '收件人',
            dataIndex: 'to',
            ellipsis: true,
        },
        {
            title: '状态',
            dataIndex: 'status',
            width: 100,
            render: (status) => getStatusTag(status),
        },
        {
            title: '重试',
            dataIndex: 'retry_count',
            width: 80,
            render: (count, record) => `${count}/${record.max_attempts || 15}`,
        },
        {
            title: '投递方式',
            dataIndex: 'delivery_method',
            width: 80,
            render: (method) => method === 'relay' ? '中继' : method === 'direct' ? '直连' : '自动',
        },
        {
            title: '创建时间',
            dataIndex: 'created_at',
            width: 160,
            render: (time) => time ? new Date(time).toLocaleString() : '-',
        },
        {
            title: '操作',
            width: 100,
            render: (_, record) => (
                <Space>
                    <Tooltip title="重试">
                        <Button
                            size="small"
                            icon={<RetweetOutlined />}
                            onClick={() => handleRetry(record.id)}
                            disabled={record.status === 'sent' || record.status === 'delivered'}
                        />
                    </Tooltip>
                    <Button size="small" onClick={() => showDetail(record)}>
                        详情
                    </Button>
                </Space>
            ),
        },
    ];

    const historyColumns = [
        {
            title: 'ID',
            dataIndex: 'id',
            width: 60,
        },
        {
            title: '队列ID',
            dataIndex: 'message_id',
            width: 100,
            render: (mid) => mid ? (
                <Tooltip title="查看投递轨迹">
                    <a onClick={() => handleViewTrace(mid)}>{mid}</a>
                </Tooltip>
            ) : '-',
        },
        {
            title: '节点',
            dataIndex: 'node_id',
            width: 150,
            ellipsis: true,
        },
        {
            title: '尝试',
            dataIndex: 'attempt_num',
            width: 60,
        },
        {
            title: '状态',
            dataIndex: 'status',
            width: 100,
            render: (status) => getStatusTag(status),
        },
        {
            title: '响应/错误',
            dataIndex: 'response',
            ellipsis: true,
            render: (text) => text || '-',
        },
        {
            title: '耗时',
            dataIndex: 'duration_ms',
            width: 80,
            render: (ms) => ms ? `${ms}ms` : '-',
        },
        {
            title: '时间',
            dataIndex: 'created_at',
            width: 160,
            render: (time) => time ? new Date(time).toLocaleString() : '-',
        },
    ];

    const renderStatsTab = () => (
        <div>
            <Row gutter={[16, 16]}>
                <Col xs={12} sm={8} md={6}>
                    <Card>
                        <Statistic
                            title="总用户数"
                            value={stats?.total_users || 0}
                            prefix={<MailOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={8} md={6}>
                    <Card>
                        <Statistic
                            title="总域名数"
                            value={stats?.total_domains || 0}
                            prefix={<InboxOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={8} md={6}>
                    <Card>
                        <Statistic
                            title="今日发送"
                            value={stats?.today_sent || 0}
                            prefix={<SendOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={8} md={6}>
                    <Card>
                        <Statistic
                            title="今日接收"
                            value={stats?.today_received || 0}
                            prefix={<InboxOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={8} md={6}>
                    <Card>
                        <Statistic
                            title="队列中"
                            value={stats?.queue_count || 0}
                            prefix={<ClockCircleOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={8} md={6}>
                    <Card>
                        <Statistic
                            title="今日失败"
                            value={stats?.today_failed || 0}
                            prefix={<CloseCircleOutlined />}
                            valueStyle={{ color: (stats?.today_failed || 0) > 0 ? '#cf1322' : undefined }}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={8} md={6}>
                    <Card>
                        <Statistic
                            title="总存储使用"
                            value={formatBytes(stats?.total_storage_used || 0)}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={8} md={6}>
                    <Card>
                        <Statistic
                            title="总邮件数"
                            value={stats?.total_messages || 0}
                        />
                    </Card>
                </Col>
            </Row>
        </div>
    );

    const renderQueueTab = () => (
        <div>
            {queueStats && (
                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                    <Col xs={12} sm={6}>
                        <Card size="small">
                            <Statistic title="等待中" value={queueStats.pending || 0} valueStyle={{ color: '#1890ff' }} />
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card size="small">
                            <Statistic title="处理中" value={queueStats.processing || 0} valueStyle={{ color: '#faad14' }} />
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card size="small">
                            <Statistic title="延迟" value={queueStats.deferred || 0} valueStyle={{ color: '#d48806' }} />
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card size="small">
                            <Statistic title="失败" value={queueStats.failed || 0} valueStyle={{ color: '#cf1322' }} />
                        </Card>
                    </Col>
                </Row>
            )}
            <Table
                columns={queueColumns}
                dataSource={queue}
                rowKey="id"
                loading={queueLoading}
                scroll={{ x: 'max-content' }}
                locale={{ emptyText: <Empty description="队列为空，暂无待发送邮件" /> }}
                pagination={{
                    ...pagination,
                    showSizeChanger: true,
                    showTotal: (total) => `共 ${total} 条`,
                    onChange: (page, pageSize) => fetchQueue(page, pageSize),
                }}
            />
        </div>
    );

    const renderHistoryTab = () => (
        <Table
            columns={historyColumns}
            dataSource={deliveryHistory}
            rowKey="id"
            loading={historyLoading}
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: <Empty description="暂无投递历史记录" /> }}
            pagination={{
                ...historyPagination,
                showSizeChanger: true,
                showTotal: (total) => `共 ${total} 条`,
                onChange: (page, pageSize) => fetchDeliveryHistory(page, pageSize),
            }}
        />
    );

    const relayColumns = [
        { title: '主机', dataIndex: 'host', render: (h, r) => <strong>{h || r.hostname || r.name || '-'}</strong> },
        { title: '端口', dataIndex: 'port', width: 80, render: (p) => p || '-' },
        {
            title: '状态',
            dataIndex: 'status',
            width: 100,
            render: (status) => {
                if (status === undefined || status === null) return '-';
                const map = {
                    up: { color: 'green', text: '在线' },
                    online: { color: 'green', text: '在线' },
                    active: { color: 'green', text: '在线' },
                    down: { color: 'red', text: '离线' },
                    offline: { color: 'red', text: '离线' },
                    disabled: { color: 'default', text: '停用' },
                };
                const s = map[String(status).toLowerCase()] || { color: 'blue', text: String(status) };
                return <Tag color={s.color}>{s.text}</Tag>;
            },
        },
        { title: '发送量', dataIndex: 'sent', width: 100, render: (v, r) => (v ?? r.total_sent ?? 0).toLocaleString() },
        { title: '失败量', dataIndex: 'failed', width: 100, render: (v, r) => (v ?? r.total_failed ?? 0).toLocaleString() },
        { title: '优先级', dataIndex: 'priority', width: 80, render: (v) => v ?? '-' },
    ];

    const renderRelayTab = () => (
        <Table
            columns={relayColumns}
            dataSource={relayHosts}
            rowKey={(r, i) => r.id ?? r.host ?? i}
            loading={relayLoading}
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: <Empty description="暂无中继主机" /> }}
            pagination={false}
        />
    );

    const renderClusterTab = () => (
        <div>
            {clusterStats ? (
                <>
                    <Row gutter={[16, 16]}>
                        <Col xs={12} sm={8} md={6}>
                            <Card><Statistic title="节点总数" value={clusterStats.total_nodes ?? clusterStats.node_count ?? 0} prefix={<NodeIndexOutlined />} /></Card>
                        </Col>
                        <Col xs={12} sm={8} md={6}>
                            <Card><Statistic title="在线节点" value={clusterStats.online_nodes ?? clusterStats.active_nodes ?? 0} valueStyle={{ color: '#3f8600' }} /></Card>
                        </Col>
                        <Col xs={12} sm={8} md={6}>
                            <Card><Statistic title="域名总数" value={clusterStats.total_domains ?? 0} prefix={<InboxOutlined />} /></Card>
                        </Col>
                        <Col xs={12} sm={8} md={6}>
                            <Card><Statistic title="用户总数" value={clusterStats.total_users ?? 0} prefix={<MailOutlined />} /></Card>
                        </Col>
                        <Col xs={12} sm={8} md={6}>
                            <Card><Statistic title="今日发送" value={clusterStats.today_sent ?? 0} prefix={<SendOutlined />} /></Card>
                        </Col>
                        <Col xs={12} sm={8} md={6}>
                            <Card><Statistic title="今日接收" value={clusterStats.today_received ?? 0} prefix={<InboxOutlined />} /></Card>
                        </Col>
                        <Col xs={12} sm={8} md={6}>
                            <Card><Statistic title="队列中" value={clusterStats.queue_count ?? 0} prefix={<ClockCircleOutlined />} /></Card>
                        </Col>
                        <Col xs={12} sm={8} md={6}>
                            <Card><Statistic title="总存储" value={formatBytes(clusterStats.total_storage_used ?? 0)} /></Card>
                        </Col>
                    </Row>
                    {Array.isArray(clusterStats.nodes) && clusterStats.nodes.length > 0 && (
                        <Table
                            style={{ marginTop: 16 }}
                            size="small"
                            rowKey={(r, i) => r.id ?? r.node_id ?? i}
                            dataSource={clusterStats.nodes}
                            pagination={false}
                            scroll={{ x: 'max-content' }}
                            columns={[
                                { title: '节点', dataIndex: 'name', render: (n, r) => n || r.node_id || r.hostname || '-' },
                                { title: '角色', dataIndex: 'role', width: 100, render: (v) => v || '-' },
                                { title: '状态', dataIndex: 'status', width: 100, render: (v) => v ? <Tag color={['up', 'online', 'active'].includes(String(v).toLowerCase()) ? 'green' : 'red'}>{v}</Tag> : '-' },
                                { title: '发送量', dataIndex: 'sent', width: 100, render: (v) => (v ?? 0).toLocaleString() },
                            ]}
                        />
                    )}
                </>
            ) : (
                !clusterStatsLoading && <Empty description="暂无集群统计数据" />
            )}
        </div>
    );

    const tabItems = [
        {
            key: 'stats',
            label: '统计概览',
            children: renderStatsTab(),
        },
        {
            key: 'queue',
            label: '发送队列',
            children: renderQueueTab(),
        },
        {
            key: 'history',
            label: '投递历史',
            children: renderHistoryTab(),
        },
        {
            key: 'relay',
            label: <span><CloudServerOutlined /> 中继主机</span>,
            children: renderRelayTab(),
        },
        {
            key: 'cluster',
            label: <span><ClusterOutlined /> 集群统计</span>,
            children: renderClusterTab(),
        },
    ];

    return (
        <Card
            title="邮件统计与队列"
            extra={
                <Space>
                    <Select
                        style={{ width: 150 }}
                        value={selectedCluster}
                        onChange={setSelectedCluster}
                        placeholder="选择集群"
                    >
                        {clusters.map(c => (
                            <Select.Option key={c.id} value={c.id}>
                                {c.name} {c.is_default && '(默认)'}
                            </Select.Option>
                        ))}
                    </Select>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={() => {
                            fetchStats();
                            if (activeTab === 'queue') fetchQueue(pagination.current, pagination.pageSize);
                            else if (activeTab === 'history') fetchDeliveryHistory(historyPagination.current, historyPagination.pageSize);
                            else fetchTabData(activeTab);
                        }}
                        loading={loading || queueLoading || historyLoading || relayLoading || clusterStatsLoading}
                    >
                        刷新
                    </Button>
                </Space>
            }
        >
            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={tabItems}
            />

            <Modal
                title="队列项详情"
                open={detailModalVisible}
                onCancel={() => setDetailModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setDetailModalVisible(false)}>关闭</Button>,
                    selectedItem && selectedItem.status !== 'sent' && selectedItem.status !== 'delivered' && (
                        <Button key="retry" type="primary" onClick={() => {
                            handleRetry(selectedItem.id);
                            setDetailModalVisible(false);
                        }}>
                            重试发送
                        </Button>
                    )
                ].filter(Boolean)}
                width={600}
            >
                {selectedItem && (
                    <Descriptions column={1} bordered size="small">
                        <Descriptions.Item label="ID">{selectedItem.id}</Descriptions.Item>
                        <Descriptions.Item label="队列ID">{selectedItem.queue_id || '-'}</Descriptions.Item>
                        <Descriptions.Item label="发件人">{selectedItem.from}</Descriptions.Item>
                        <Descriptions.Item label="收件人">{selectedItem.to}</Descriptions.Item>
                        <Descriptions.Item label="状态">{getStatusTag(selectedItem.status)}</Descriptions.Item>
                        <Descriptions.Item label="投递方式">{selectedItem.delivery_method === 'relay' ? '中继' : selectedItem.delivery_method === 'direct' ? '直连' : '自动'}</Descriptions.Item>
                        <Descriptions.Item label="重试次数">{selectedItem.retry_count || 0} / {selectedItem.max_attempts || 15}</Descriptions.Item>
                        <Descriptions.Item label="下次重试">{selectedItem.next_retry_at ? new Date(selectedItem.next_retry_at).toLocaleString() : '-'}</Descriptions.Item>
                        <Descriptions.Item label="最后错误">{selectedItem.last_error || '-'}</Descriptions.Item>
                        <Descriptions.Item label="创建时间">{selectedItem.created_at ? new Date(selectedItem.created_at).toLocaleString() : '-'}</Descriptions.Item>
                        <Descriptions.Item label="更新时间">{selectedItem.updated_at ? new Date(selectedItem.updated_at).toLocaleString() : '-'}</Descriptions.Item>
                    </Descriptions>
                )}
            </Modal>

            {/* Message delivery trace Drawer */}
            <Drawer
                title={`投递轨迹 - 邮件 #${traceMessageId ?? ''}`}
                open={traceDrawerVisible}
                onClose={() => setTraceDrawerVisible(false)}
                width={560}
                extra={
                    <Button icon={<ReloadOutlined />} loading={traceLoading} onClick={() => traceMessageId && handleViewTrace(traceMessageId)}>
                        刷新
                    </Button>
                }
            >
                {traceLoading ? (
                    <div style={{ textAlign: 'center', padding: 40 }}><ReloadOutlined spin style={{ fontSize: 24 }} /></div>
                ) : traceRecords.length > 0 ? (
                    <Timeline
                        items={traceRecords.map((t, i) => ({
                            key: i,
                            color: ['sent', 'delivered'].includes(t.status) ? 'green' : ['failed', 'bounced'].includes(t.status) ? 'red' : 'blue',
                            children: (
                                <div>
                                    <Space>
                                        {getStatusTag(t.status)}
                                        {t.attempt_num != null && <Tag>第 {t.attempt_num} 次</Tag>}
                                        {t.duration_ms != null && <span style={{ color: '#999', fontSize: 12 }}>{t.duration_ms}ms</span>}
                                    </Space>
                                    <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                                        节点: {t.node_id || '-'} · {t.created_at ? new Date(t.created_at).toLocaleString() : '-'}
                                    </div>
                                    {t.response && (
                                        <div style={{ fontSize: 12, marginTop: 4, wordBreak: 'break-all' }}>{t.response}</div>
                                    )}
                                </div>
                            ),
                        }))}
                    />
                ) : (
                    <Empty description="暂无投递轨迹" />
                )}
            </Drawer>
        </Card>
    );
};

export default Stats;
